import type { IncidentFormData } from './rapportsCyberService';

const API_BASE = '/api/prisme/rapports-cyber';

export interface GenerateRapportIncidentPayload {
  clientId: number;
  prismeClientName?: string;
  servo2ClientName?: string;
  formId?: string | number;
  formData?: IncidentFormData;
  qualityLevel?: number;
  formLabel?: string;
}

export interface GenerateRapportIncidentResponse {
  success: boolean;
  documentId?: number;
  data?: any;
  jobId?: string;
  status?: 'running' | 'complete' | 'error';
  error?: string;
}

async function getRapportIncidentJobStatus(jobId: string): Promise<GenerateRapportIncidentResponse> {
  const res = await fetch(`${API_BASE}/generer-rapport-incident/status/${encodeURIComponent(jobId)}`, {
    credentials: 'include',
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(err.error || 'Erreur statut génération du rapport incident');
  }
  return (await res.json()) as GenerateRapportIncidentResponse;
}

export async function generateRapportIncidentFromServo2(
  payload: GenerateRapportIncidentPayload
): Promise<GenerateRapportIncidentResponse> {
  const res = await fetch(`${API_BASE}/generer-rapport-incident?useJob=1`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(payload),
  });

  // Mode job: 202 -> { jobId }
  if (res.status === 202) {
    const data = (await res.json()) as { jobId?: string };
    if (!data.jobId) throw new Error('JobId manquant pour la génération du rapport incident');

    const jobId = data.jobId;
    // Polling avec backoff (court): Cloudflare/Proxy safe
    let delayMs = 1500;
    const maxDelayMs = 8000;
    const maxTotalMs = 12 * 60 * 1000; // 12 minutes
    const startedAt = Date.now();

    // eslint-disable-next-line no-constant-condition
    while (true) {
      if (Date.now() - startedAt > maxTotalMs) {
        throw new Error('Délai dépassé lors de la génération du rapport (timeout)');
      }
      await new Promise((r) => setTimeout(r, delayMs));
      const status = await getRapportIncidentJobStatus(jobId);
      if (status.status === 'complete') {
        return { success: true, documentId: status.documentId };
      }
      if (status.status === 'error') {
        throw new Error(status.error || 'Erreur génération du rapport incident');
      }
      delayMs = Math.min(maxDelayMs, Math.round(delayMs * 1.5));
    }
  }

  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(err.error || 'Erreur génération du rapport incident');
  }

  return (await res.json()) as GenerateRapportIncidentResponse;
}

